"use client";

import * as React from "react";
import { GlowingEffect } from "./GlowingEffect";
import { Card } from "./card";
import { Calendar as CalendarIcon, ShieldCheck, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";

declare global {
  interface Window {
    Cal: any;
  }
}

export interface CalendarSchedulerProps {
  id?: string;
  minimal?: boolean;
  onConfirm?: (value: { name: string; email: string; date: string }) => void;
}

function CalendarSchedulerSkeleton() {
  return (
    <div className="w-full h-full flex animate-pulse overflow-hidden rounded-[48px] bg-[#111111] border border-white/5">
      <div className="w-1/3 border-r border-white/5 p-8 space-y-6 hidden md:block">
        <div className="size-12 rounded-xl bg-white/5" />
        <div className="h-6 w-3/4 bg-white/5 rounded-lg" />
        <div className="pt-8 space-y-4">
          <div className="h-10 w-full bg-white/5 rounded-xl" />
          <div className="h-10 w-full bg-white/5 rounded-xl" />
        </div>
      </div>
      <div className="flex-1 p-8 space-y-8">
        <div className="flex justify-between items-center">
          <div className="h-6 w-32 bg-white/5 rounded-lg" />
        </div>
        <div className="grid grid-cols-7 gap-4">
          {Array.from({ length: 21 }).map((_, i) => (
            <div key={i} className="aspect-square bg-white/5 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

function CalendarScheduler({ 
  id = "my-cal-inline-strategyacall", 
  minimal = false,
  onConfirm 
}: CalendarSchedulerProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const calendarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    (function (C: any, A: string, L: string) {
      let p = function (a: any, ar: any) {
        a.q.push(ar);
      };
      let d = C.document;
      if (C.Cal) {
        if (C.Cal.ns && C.Cal.ns.strategyacall) return;
      }
      
      C.Cal =
        C.Cal ||
        function () {
          let cal = C.Cal;
          let ar = arguments;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            const s = d.createElement("script");
            s.src = A;
            d.head.appendChild(s);
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api: any = function () {
              p(api, arguments);
            };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    const cal = window.Cal;
    if (cal && calendarRef.current) {
      cal("init", "strategyacall", { origin: "https://app.cal.com" });

      cal.ns.strategyacall("inline", {
        elementOrSelector: calendarRef.current,
        config: { layout: "month_view" },
        calLink: "aureos.app/strategyacall",
      });

      cal.ns.strategyacall("ui", {
        theme: "dark",
        cssVarsPerTheme: {
          light: { "cal-brand": "#000000" },
          dark: { "cal-brand": "#fafafa" },
        },
        hideEventTypeDetails: false,
        layout: "month_view",
      });
      
      cal.ns.strategyacall("on", {
        action: "linkReady",
        callback: () => {
          setTimeout(() => setIsLoading(false), 500);
        }
      });

      cal.ns.strategyacall("on", {
        action: "bookingSuccessful",
        callback: (e: any) => {
          if (onConfirm) {
            const payload = e?.data?.payload || e?.payload || e || {};
            onConfirm({
              name: payload.name || "Member",
              email: payload.email || "",
              date: payload.startTime || "Confirmed",
            });
          }
        },
      });
    }
    
    const safetyTimer = setTimeout(() => setIsLoading(false), 5000);
    return () => clearTimeout(safetyTimer);
  }, [onConfirm]);

  if (minimal) {
    return (
      <div className="w-full h-full min-h-[700px] relative group">
        <AnimatePresence mode="wait">
          {isLoading && (
            <motion.div 
              key="skeleton"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="absolute inset-0 z-20"
            >
              <CalendarSchedulerSkeleton />
            </motion.div>
          )}
        </AnimatePresence>
        <div
          id={id}
          ref={calendarRef}
          className={cn(
            "w-full h-full min-h-[700px] rounded-[48px] overflow-hidden bg-background/20 backdrop-blur-3xl border border-white/5 shadow-2xl transition-opacity duration-700",
            isLoading ? "opacity-0" : "opacity-100"
          )}
        />
      </div>
    );
  }

  return (
    <div
      className="relative p-1 w-full flex justify-center group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <GlowingEffect
        spread={120}
        glow={isHovered}
        disabled={false}
        proximity={200}
        inactiveZone={0.01}
        borderWidth={1.5}
      />

      <Card className="w-full max-w-4xl shadow-2xl border-border/40 bg-card/40 backdrop-blur-3xl rounded-[48px] overflow-hidden transition-all duration-500 group-hover:border-primary/20">
        <div className="pt-12 px-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <div className="size-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <CalendarIcon size={16} />
              </div>
              <span className="text-[10px] font-bold tracking-[0.4em] uppercase text-primary">Live Availability</span>
            </div>
            <h2 className="text-3xl font-light tracking-tight">
              Aure <span className="italic">Strategy Session</span>
            </h2>
          </div>
          <div className="px-4 py-2 rounded-2xl bg-muted/20 border border-border/40 flex items-center gap-3">
            <ShieldCheck size={14} className="text-emerald-500" />
            <span className="text-[9px] font-bold tracking-widest uppercase text-muted-foreground">Encrypted Channel</span>
          </div>
        </div>

        <div className="p-4 md:p-8 h-[650px] w-full relative">
          <AnimatePresence mode="wait">
            {isLoading && (
              <motion.div 
                key="skeleton-inline"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-4 md:inset-8 z-20 flex flex-col items-center justify-center bg-black/50 rounded-[32px] backdrop-blur-sm"
              >
                <Loader2 className="size-10 animate-spin text-primary opacity-40 mb-4" />
                <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-muted-foreground">Initializing Scheduler...</p>
              </motion.div>
            )}
          </AnimatePresence>
          <div
            id={id}
            ref={calendarRef}
            className={cn(
              "w-full h-full rounded-[32px] overflow-hidden border border-border/10 bg-background/20 custom-scrollbar transition-opacity duration-700",
              isLoading ? "opacity-0" : "opacity-100"
            )}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <div className="p-8 border-t border-border/10 flex flex-col md:flex-row items-center justify-between bg-muted/5 gap-4">
          <div className="text-[8px] tracking-[0.5em] uppercase text-muted-foreground/30 font-bold">
            Portal Version 2.4.0-Stable
          </div>
          <div className="flex items-center gap-4">
             <div className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
             <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-muted-foreground/40">Engineers Online</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

export { CalendarScheduler };